import { useEffect, useRef, useState } from "react";
import { X, Camera, Move } from "lucide-react";

/**
 * Simulated AR fallback — opens device camera and overlays the tinted bag image.
 * The bag is draggable + pinch/scrollwheel zoomable.
 */
export const SimulatedAR = ({ color, onClose }) => {
  const videoRef = useRef(null);
  const [error, setError] = useState(null);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [scale, setScale] = useState(1);
  const dragRef = useRef(null);
  const pinchRef = useRef(null);

  useEffect(() => {
    let stream;
    (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: "environment" } },
          audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
      } catch (e) {
        setError(
          "Camera access not available. Allow camera in your browser settings, or use a phone."
        );
      }
    })();
    return () => {
      if (stream) stream.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const onTouchStart = (e) => {
    if (e.touches.length === 2) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      pinchRef.current = { dist: Math.hypot(dx, dy), scale };
    } else if (e.touches.length === 1) {
      dragRef.current = {
        x: e.touches[0].clientX - pos.x,
        y: e.touches[0].clientY - pos.y,
      };
    }
  };
  const onTouchMove = (e) => {
    if (e.touches.length === 2 && pinchRef.current) {
      const dx = e.touches[0].clientX - e.touches[1].clientX;
      const dy = e.touches[0].clientY - e.touches[1].clientY;
      const dist = Math.hypot(dx, dy);
      const next = Math.max(0.4, Math.min(2.4, pinchRef.current.scale * (dist / pinchRef.current.dist)));
      setScale(next);
    } else if (e.touches.length === 1 && dragRef.current) {
      setPos({
        x: e.touches[0].clientX - dragRef.current.x,
        y: e.touches[0].clientY - dragRef.current.y,
      });
    }
  };
  const onTouchEnd = () => {
    pinchRef.current = null;
    dragRef.current = null;
  };

  const onMouseDown = (e) => {
    dragRef.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
  };
  const onMouseMove = (e) => {
    if (!dragRef.current) return;
    setPos({ x: e.clientX - dragRef.current.x, y: e.clientY - dragRef.current.y });
  };
  const onMouseUp = () => (dragRef.current = null);
  const onWheel = (e) => {
    setScale((s) => Math.max(0.4, Math.min(2.4, s - e.deltaY * 0.0015)));
  };

  return (
    <div
      data-testid="simulated-ar"
      className="fixed inset-0 z-50 bg-black"
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
    >
      {error ? (
        <div className="absolute inset-0 flex flex-col items-center justify-center text-white p-8 text-center">
          <Camera size={28} className="mb-4 opacity-70" />
          <p className="font-serif text-2xl mb-2">Camera unavailable</p>
          <p className="kb-mono-label text-white/70 max-w-xs">{error}</p>
          <button
            data-testid="ar-close-error"
            className="kb-btn-ghost mt-6 !text-white !border-white"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      ) : (
        <video
          ref={videoRef}
          playsInline
          muted
          className="absolute inset-0 w-full h-full object-cover"
        />
      )}

      {/* Bag overlay */}
      <div
        data-testid="ar-bag-overlay"
        onMouseDown={onMouseDown}
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        onWheel={onWheel}
        className="absolute left-1/2 top-1/2 cursor-grab active:cursor-grabbing"
        style={{
          transform: `translate(calc(-50% + ${pos.x}px), calc(-50% + ${pos.y}px)) scale(${scale})`,
          width: "62vmin",
          height: "62vmin",
        }}
      >
        <div
          className="kb-bag w-full h-full"
          style={{
            "--kb-tint": `${color.filter} drop-shadow(0 30px 30px rgba(0,0,0,0.45))`,
          }}
        />
      </div>

      {/* HUD */}
      <div className="absolute top-0 inset-x-0 p-5 flex items-center justify-between text-white">
        <div className="kb-mono-label">SIMULATED AR · {color.name}</div>
        <button
          data-testid="ar-close"
          className="p-2 rounded-full bg-white/10 backdrop-blur"
          onClick={onClose}
          aria-label="Close AR"
        >
          <X size={18} />
        </button>
      </div>
      <div className="absolute bottom-6 inset-x-0 flex justify-center">
        <div className="kb-mono-label text-white/80 px-4 py-2 bg-black/40 backdrop-blur flex items-center gap-2">
          <Move size={12} /> Drag · Pinch to scale
        </div>
      </div>
    </div>
  );
};
